import { ShiftOptions } from 'models/ICattlesData'
import {
	IMilkingProcessDeviationEntry,
	IMilkingRoutineEvent,
	IMilkingUnitOverlay,
	MilkingProcessDeviationType,
	MilkingRoutineEventType,
} from 'models/MilkingProcess'

type MilkingOverlayMockMode = '1' | '2' | '3' | '4'

const MILKING_OVERLAY_VIDEO_MOCK_URL =
	'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4'

const COWVISION_API = 'https://api.cowvision.ru/'
const FAKE_ENTRIES_COUNT = 3

const isMilkingOverlayMockAllowed = () =>
	process.env.NODE_ENV === 'development' ||
	process.env.REACT_APP_SERVER_NAME === COWVISION_API

export const getMilkingOverlayMockMode = (): MilkingOverlayMockMode | null => {
	if (typeof window === 'undefined' || !isMilkingOverlayMockAllowed()) {
		return null
	}
	const value = new URLSearchParams(window.location.search).get(
		'milkingOverlayMock',
	)
	if (value === '1' || value === '2' || value === '3' || value === '4')
		return value
	return null
}

const routineEvents: { type: MilkingRoutineEventType; name: string }[] = [
	{ type: 'pre_milking_treatment', name: 'Обработка вымени' },
	{ type: 'stimulation', name: 'Стимуляция' },
	{ type: 'wiping', name: 'Вытирание' },
	{ type: 'connecting', name: 'Подключение' },
	{ type: 'disconnecting', name: 'Отключение' },
	{ type: 'disinfection', name: 'Дезинфекция' },
]

const skippedEventByDeviation: Partial<
	Record<MilkingProcessDeviationType, MilkingRoutineEventType>
> = {
	issue_pre_milking_treatment: 'pre_milking_treatment',
	issue_stimulation: 'stimulation',
	issue_wiping: 'wiping',
	issue_disinfection: 'disinfection',
}

const fakeDeviationTypes: MilkingProcessDeviationType[] = [
	'issue_stimulation',
	'issue_pre_milking_treatment',
	'issue_early_connecting',
]

const overlayRight: IMilkingUnitOverlay = {
	rect: { x: 640, y: 60, width: 430, height: 600 },
	text: { x: 1110, y: 100 },
}

const overlayLeft: IMilkingUnitOverlay = {
	rect: { x: 1380, y: 80, width: 420, height: 580 },
	text: { x: 1340, y: 120 },
}

const overlayBottom: IMilkingUnitOverlay = {
	rect: { x: 1185, y: 886, width: 358, height: 193 },
	text: null,
}

const getMockOverlay = (
	mode: MilkingOverlayMockMode,
	index: number,
): IMilkingUnitOverlay => {
	if (mode === '1') return overlayRight
	if (mode === '2') return overlayLeft
	if (mode === '4') return overlayBottom
	const base = index % 2 === 0 ? overlayRight : overlayLeft
	return { ...base, text: null }
}

const getMockEvents = (
	entry: IMilkingProcessDeviationEntry,
): IMilkingRoutineEvent[] => {
	const start = entry.timestamp - 90 * 1000
	const skippedType = skippedEventByDeviation[entry.deviation.type]
	const isConnectingDeviation =
		entry.deviation.type === 'issue_early_connecting' ||
		entry.deviation.type === 'issue_later_connecting'

	return routineEvents.map(({ type, name }, index) => {
		if (type === skippedType) {
			return {
				type,
				name,
				timestamp: null,
				status: 'skipped' as const,
				length_seconds: 20,
			}
		}
		return {
			type,
			name,
			timestamp: start + index * 20 * 1000,
			status: 'done' as const,
			length_seconds:
				type === 'connecting' && isConnectingDeviation
					? (entry.deviation.length_seconds ?? 49)
					: null,
		}
	})
}

const createFakeEntries = (): IMilkingProcessDeviationEntry[] =>
	Array.from({ length: FAKE_ENTRIES_COUNT }, (_, index) => ({
		id: -(index + 1),
		timestamp: Date.now() - index * 10 * 60 * 1000,
		milking_hall: 'Карусель',
		period: ShiftOptions.AM,
		milking_unit_num: 6 + index,
		deviation: {
			type: fakeDeviationTypes[index],
			length_seconds:
				fakeDeviationTypes[index] === 'issue_early_connecting' ? 49 : null,
		},
		video_url: MILKING_OVERLAY_VIDEO_MOCK_URL,
		milker: 'Иванов Иван',
		is_false: false,
		is_false_by: null,
	}))

export const applyMilkingOverlayMock = (
	entries: IMilkingProcessDeviationEntry[] | null | undefined,
	mode: MilkingOverlayMockMode | null,
): IMilkingProcessDeviationEntry[] | null | undefined => {
	if (!mode) return entries
	const source = entries?.length ? entries : createFakeEntries()

	return source.map((entry, index) => ({
		...entry,
		video_url: entry.video_url || MILKING_OVERLAY_VIDEO_MOCK_URL,
		video_overlay: getMockOverlay(mode, index),
		milking_events: getMockEvents(entry),
	}))
}
